import React, { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Alert
} from '@mui/material';
import AppDialog, { DialogField } from '../../common/AppDialog';
import ConditionGroupsEditor from '../../common/ConditionGroupsEditor';

const RuleFormDialog = ({
  open,
  onClose,
  editRule,
  categories,
  availableFields,
  onSubmit
}) => {
  // If editRule exists but doesn't have an _id (or id), treat it as a prefilled new rule
  const isEditing = Boolean(editRule && (editRule._id || editRule.id));
  // `name` is kept in state (but not shown) so editing preserves the stored
  // name; the backend generates one for new rules.
  const [formData, setFormData] = useState({
    name: '',
    conditionGroups: [
      { conditions: [{ field: 'description', operator: 'contains', value: '' }] }
    ],
    categoryId: '',
    priority: 1
  });

  const [result, setResult] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  // Initialize form data when dialog opens or editRule changes
  useEffect(() => {
    if (open) {
      if (editRule) {
        // Handle both old and new format rules
        if (editRule.conditionGroups && editRule.conditionGroups.length > 0) {
          setFormData({
            name: editRule.name || '',
            conditionGroups: [...editRule.conditionGroups],
            categoryId: editRule.categoryId,
            priority: editRule.priority
          });
        } else {
          // Convert old format to new format
          setFormData({
            name: editRule.name || '',
            conditionGroups: [{ conditions: [...(editRule.conditions || [])] }],
            categoryId: editRule.categoryId,
            priority: editRule.priority
          });
        }
      } else {
        setFormData({
          name: '',
          conditionGroups: [
            { conditions: [{ field: 'description', operator: 'contains', value: '' }] }
          ],
          categoryId: '',
          priority: 1
        });
      }
      setResult(null);
      setSubmitDisabled(false);
    }
  }, [open, editRule]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.categoryId) {
      return 'Please select a category';
    }

    if (!formData.conditionGroups.length) {
      return 'At least one condition group is required';
    }

    for (const group of formData.conditionGroups) {
      if (!group.conditions.length) {
        return 'All condition groups must have at least one condition';
      }

      for (const condition of group.conditions) {
        if (!condition.value.trim()) {
          return 'All conditions must have valid values';
        }

        if (condition.operator === 'between') {
          const [min, max] = condition.value.split(',').map(Number);
          if (isNaN(min) || isNaN(max) || min >= max) {
            return 'Between values must be min,max with min<max';
          }
        }
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const err = validateForm();
    if (err) {
      setResult({ success: false, message: err });
      return;
    }
    setSubmitDisabled(true);

    const payload = {
      ...formData,
      priority: Number(formData.priority),
      conditions: formData.conditionGroups[0]?.conditions || []
    };

    try {
      await onSubmit(payload, editRule);
      setResult({ success: true, message: editRule ? 'Rule updated' : 'Rule created' });
      setTimeout(() => {
        onClose();
        setSubmitDisabled(false);
      }, 800);
    } catch (error) {
      setResult({ success: false, message: 'Failed to save rule' });
      setSubmitDisabled(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Rule' : 'Add Rule'}
      subtitle="Auto-categorize matching transactions"
      primaryLabel={submitDisabled ? (isEditing ? 'Saving…' : 'Adding…') : (isEditing ? 'Save' : 'Add')}
      onPrimary={handleSubmit}
      primaryDisabled={submitDisabled}
    >
      {/* Category + Priority share a row — both short, and together they read
          as "categorize as X, weighted Y". */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <DialogField label="Category" required sx={{ mt: 0, flex: 1.4, minWidth: 0 }}>
          <FormControl fullWidth size="small">
            <Select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleChange}
              displayEmpty
              renderValue={selected => {
                if (!selected || selected === '') {
                  return <span style={{ color: '#999' }}>Select a category</span>;
                }
                const cat = categories.find(c => c.id === selected);
                if (cat) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color, flexShrink: 0 }} />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</Box>
                    </Box>
                  );
                }
                return null;
              }}
            >
              <MenuItem value="" sx={{ color: '#999', fontSize: '0.875rem' }}>
                Select a category
              </MenuItem>
              {categories.map(c => (
                <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: c.color, flexShrink: 0 }} />
                    {c.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogField>

        <DialogField label="Priority" sx={{ mt: 0, flex: 1, minWidth: 0 }}>
          <FormControl fullWidth size="small">
            <Select name="priority" value={formData.priority} onChange={handleChange}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <MenuItem key={num} value={num} sx={{ fontSize: '0.875rem' }}>
                  {num}{num === 1 ? ' (Highest)' : num === 10 ? ' (Lowest)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogField>
      </Box>

      <DialogField label="Matching Conditions">
        <ConditionGroupsEditor
          groups={formData.conditionGroups}
          onChange={(conditionGroups) => setFormData(prev => ({ ...prev, conditionGroups }))}
          availableFields={availableFields}
        />
      </DialogField>

      {result && !result.success && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {result.message}
        </Alert>
      )}
    </AppDialog>
  );
};

export default RuleFormDialog;
